import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Map, ZoomControl, Overlay } from 'pigeon-maps';
import { BsSearch } from 'react-icons/bs';
import { TbWheelchair } from 'react-icons/tb';
import { FaRegCompass } from 'react-icons/fa';
import { MdSatelliteAlt, MdMap } from 'react-icons/md';
import { getAirportUuidAndCoordinates, SelectCurrentCoordinates } from '_redux/slices/timeline/timeline-live-location-tab';
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';
import {
    buildCentroids,
    resolveDevice,
    deviceStateLabel,
    OUT_OF_RANGE_KEY,
    UNRESOLVED_KEY,
    POS_BADGE,
    SOURCE_BADGE,
    LEGEND_ITEMS,
    pickDominantColor,
} from './asset-tracking-utils';
import { selectAirportLocations } from '_redux/slices/timeline/timeline-areas';

const SATELLITE_PROVIDER = (x, y, z) =>
    `https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;
const OSM_PROVIDER = (x, y, z) =>
    `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;

const DEVICE_ICONS = {
    'Wheelchair-Tracking': TbWheelchair,
    'carts': TbWheelchair,
};

// Location list helpers
function sourceBadgeText(source, lvpType, distance) {
    const lvpLabel = lvpType === 'bluetooth' ? 'BLE' : lvpType === 'gps' ? 'GPS' : lvpType === 'wifi' ? 'WiFi' : null;
    if (source === 'gps' && distance != null)  return `GPS · ${distance} m`;
    if (source === 'wifi' && distance != null) return `WiFi · ${distance} m`;
    if (source === 'last-known') {
        let t = lvpLabel ? `Last known · ${lvpLabel}` : 'Last known';
        if (distance != null) t += ` · ${distance} m`;
        return t;
    }
    const badge = SOURCE_BADGE[source];
    return badge?.label ?? source;
}

// LocationGroup
function LocationGroup({ title, items, variant = 'default', subtitle, selectedEui, onSelectDevice, listRowRefs, blinkEui, onBlinkEnd }) {
    const [expanded, setExpanded] = useState(true);

    const HEADER = {
        default: { bg: '#f0f6ff', border: '#dbeafe', titleColor: '#1e3a5f' },
        warning: { bg: '#fffbeb', border: '#fde68a', titleColor: '#78350f' },
        muted:   { bg: '#f8fafc', border: '#e2e8f0', titleColor: '#475569' },
    };
    const { bg, border, titleColor } = HEADER[variant];
    const badgeBg = variant === 'warning' ? '#f59e0b' : variant === 'muted' ? '#94a3b8' : '#307FE2';

    return (
        <div style={{ border: `1px solid ${border}`, borderRadius: 10, marginBottom: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(48,127,226,0.07)' }}>
            <div
                onClick={() => setExpanded(p => !p)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: bg, borderBottom: expanded ? `1px solid ${border}` : 'none', cursor: 'pointer', userSelect: 'none' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={titleColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ flexShrink: 0, transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}>
                        <polyline points="9 18 15 12 9 6" />
                    </svg>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: titleColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
                        {subtitle && <div style={{ fontSize: '0.68rem', color: titleColor, opacity: 0.6, marginTop: 1 }}>{subtitle}</div>}
                    </div>
                </div>
                <span style={{ background: badgeBg, color: '#fff', fontWeight: 700, fontSize: '0.68rem', padding: '2px 9px', borderRadius: 20, flexShrink: 0, marginLeft: 8 }}>
                    {items.length}
                </span>
            </div>

            {expanded && (
                <div style={{ background: '#fff' }}>
                    {items.map(({ device, markerColor, source, lvpType, distance, nearestTitle, stale }, idx) => {
                        const eui        = device?.meta?.deviceEui;
                        const isSelected = eui === selectedEui;
                        const isBlinking = eui === blinkEui;
                        const stateLabel = deviceStateLabel(device);
                        const badge    = SOURCE_BADGE[source] ?? SOURCE_BADGE['last-known'];
                        const badgeText = sourceBadgeText(source, lvpType, distance);
                        const isLast   = idx === items.length - 1;

                        return (
                            <div
                                key={eui || idx}
                                ref={node => { if (eui) listRowRefs.current[eui] = node; }}
                                onClick={() => onSelectDevice(eui)}
                                className={isBlinking ? 'device-row-blink' : ''}
                                onAnimationEnd={() => { if (isBlinking) onBlinkEnd(); }}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '7px 12px',
                                    borderBottom: isLast ? 'none' : '1px solid #f0f6ff',
                                    borderLeft: `3px solid ${markerColor}`,
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    transition: 'background 0.1s',
                                }}
                                onMouseEnter={e => { if (!isBlinking) e.currentTarget.style.background = '#f8faff'; }}
                                onMouseLeave={e => { if (!isBlinking) e.currentTarget.style.background = 'transparent'; }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: markerColor, flexShrink: 0 }} />
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e3a5f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {device?.meta?.deviceName}
                                            </span>
                                            {stale && (
                                                <span style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.58rem', fontWeight: 700, padding: '1px 5px', borderRadius: 20, flexShrink: 0 }}>5m+</span>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
                                            <span style={{ fontSize: '0.7rem', color: '#64748b' }}>{stateLabel}</span>
                                            {nearestTitle && (
                                                <span style={{ fontSize: '0.67rem', color: '#94a3b8' }}>· Nearest: {nearestTitle}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <span style={{ flexShrink: 0, marginLeft: 8, background: badge.bg, color: badge.color, fontSize: '0.65rem', fontWeight: 700, padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                                    {badgeText}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// Main component
function AssetTrackingLiveView({ devices, gateways, wifiLocations }) {

    const resolveCache = useRef({});

    // Shared state
    const [now, setNow]               = useState(Date.now());
    const [selectedEui, setSelectedEui] = useState(null);
    const [blinkEui, setBlinkEui]     = useState(null);

    // Map state
    const [mapCenter, setMapCenter]   = useState([0, 0]);
    const [zoom, setZoom]             = useState(18);
    const [mapBounds, setMapBounds]   = useState(null);
    const [activeKey, setActiveKey]   = useState(null);
    const [clusterModal, setClusterModal] = useState(null);
    const [satelliteView, setSatelliteView] = useState(false);

    // List state
    const [searchTerm, setSearchTerm]         = useState('');
    const [debounceSearch, setDebounceSearch] = useState('');
    const listRowRefs = useRef({});

    const dispatch = useDispatch();
    const dbUser             = useSelector(selectFirebaseInfo);
    const airportCoordinates = useSelector(SelectCurrentCoordinates);
    const locations          = useSelector(selectAirportLocations) ?? [];

    // Effects
    useEffect(() => {
        if (!airportCoordinates?.lat) {
            dispatch(getAirportUuidAndCoordinates(dbUser?.details?.authority));
        }
    }, []);

    useEffect(() => {
        if (airportCoordinates?.lat && airportCoordinates?.lon) {
            setMapCenter([parseFloat(airportCoordinates.lat), parseFloat(airportCoordinates.lon)]);
        }
    }, [airportCoordinates]);

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 10000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        const id = setTimeout(() => setDebounceSearch(searchTerm.trim().toLowerCase()), 200);
        return () => clearTimeout(id);
    }, [searchTerm]);

    // Central resolution
    const centroids = useMemo(() => buildCentroids(locations), [locations]);

    // Single pass — every downstream derivation reads from this.
    // Per-device cache: only recalculates the device whose Firebase doc actually changed.
    const resolvedAll = useMemo(() => {
        const next = {};
        const results = (devices || []).map(d => {
            const eui = d?.id;
            const devKey = d?.latest?.at?.toMillis?.() ?? 0;
            const wKey   = (wifiLocations || {})[eui]?.resolvedAt ?? 0;
            const cached = resolveCache.current[eui];
            if (
                cached &&
                cached.devKey  === devKey  &&
                cached.wKey    === wKey    &&
                cached.loc     === locations &&
                cached.gw      === gateways &&
                cached.now     === now
            ) {
                next[eui] = cached;
                return cached.result;
            }
            const result = resolveDevice(d, locations, centroids, wifiLocations || {}, gateways || [], now);
            next[eui] = { devKey, wKey, loc: locations, gw: gateways, now, result };
            return result;
        });
        resolveCache.current = next;
        return results;
    }, [devices, locations, centroids, wifiLocations, gateways, now]);

    // Locations list derivations
    const locGroups = useMemo(() => {
        const groups = {};
        for (const r of resolvedAll) {
            if (!groups[r.locationKey]) groups[r.locationKey] = [];
            groups[r.locationKey].push(r);
        }
        return groups;
    }, [resolvedAll]);

    const namedEntries = useMemo(() => {
        const entries = Object.entries(locGroups).filter(
            ([k]) => k !== OUT_OF_RANGE_KEY && k !== UNRESOLVED_KEY
        );
        entries.sort((a, b) => b[1].length - a[1].length);
        if (!debounceSearch) return entries;
        return entries.filter(([title, items]) =>
            title.toLowerCase().includes(debounceSearch) ||
            items.some(i => i.device?.meta?.deviceName?.toLowerCase().includes(debounceSearch))
        );
    }, [locGroups, debounceSearch]);

    const outOfRange = locGroups[OUT_OF_RANGE_KEY] ?? [];
    const unresolved = locGroups[UNRESOLVED_KEY]   ?? [];
    const totalAssets  = devices?.length ?? 0;
    const resolvedCount = totalAssets - unresolved.length;

    // Map derivations
    const mapGroups = useMemo(() => {
        const groups = {};
        for (const r of resolvedAll) {
            if (r.lat == null || r.lon == null) continue;
            const key = `${r.lat.toFixed(5)},${r.lon.toFixed(5)}`;
            if (!groups[key]) groups[key] = { lat: r.lat, lon: r.lon, items: [] };
            groups[key].items.push(r);
        }
        return groups;
    }, [resolvedAll]);

    const visibleMapGroups = useMemo(() => {
        if (!mapBounds) return mapGroups;
        const latPad = (mapBounds.ne[0] - mapBounds.sw[0]) * 0.1;
        const lngPad = (mapBounds.ne[1] - mapBounds.sw[1]) * 0.1;
        const filtered = {};
        for (const [key, group] of Object.entries(mapGroups)) {
            const inBounds =
                group.lat >= mapBounds.sw[0] - latPad &&
                group.lat <= mapBounds.ne[0] + latPad &&
                group.lon >= mapBounds.sw[1] - lngPad &&
                group.lon <= mapBounds.ne[1] + lngPad;
            const hasSelected = selectedEui && group.items.some(r => r.device?.meta?.deviceEui === selectedEui);
            if (inBounds || hasSelected) filtered[key] = group;
        }
        return filtered;
    }, [mapGroups, mapBounds, selectedEui]);

    const provider = useMemo(
        () => (satelliteView ? SATELLITE_PROVIDER : OSM_PROVIDER),
        [satelliteView]
    );

    const deviceMarkers = useMemo(() => {
        const entries = Object.entries(visibleMapGroups);

        // Coord key of the currently selected device (for cluster highlight)
        const selectedCoordKey = selectedEui
            ? (() => {
                const r = resolvedAll.find(x => x.device?.meta?.deviceEui === selectedEui);
                return r?.lat != null ? `${r.lat.toFixed(5)},${r.lon.toFixed(5)}` : null;
            })()
            : null;

        // Active / highlighted marker last → renders on top
        const priorityKey = activeKey ?? selectedCoordKey;
        if (priorityKey) {
            const idx = entries.findIndex(([k]) => k === priorityKey);
            if (idx > -1) entries.push(entries.splice(idx, 1)[0]);
        }
        return entries.map(([coordKey, group]) => {
            const { lat, lon, items } = group;
            const isOpen  = activeKey === coordKey;
            const Icon    = DEVICE_ICONS[items[0]?.device?.meta?.deviceType] ?? TbWheelchair;

            if (items.length === 1) {
                const { device, markerColor, info, stale, positioningType: pt } = items[0];
                const ptBadge  = POS_BADGE[pt];
                const deviceName = device?.meta?.deviceName;
                return (
                    <Overlay key={coordKey} anchor={[lat, lon]} offset={[0, 0]}>
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                const opening = activeKey !== coordKey;
                                setActiveKey(opening ? coordKey : null);
                                setSelectedEui(opening ? device?.meta?.deviceEui : null);
                                if (opening) setBlinkEui(device?.meta?.deviceEui);
                            }}
                            style={{ cursor: 'pointer', transform: 'translate(-50%, -100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'auto', position: 'relative', zIndex: isOpen ? 200 : 1 }}
                        >
                            {stale && (
                                <div className="stale-badge">
                                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                                    </svg>
                                    5m+
                                </div>
                            )}
                            <div className="map-marker" style={{ background: markerColor, outline: isOpen ? '3px solid #1B4F8A' : 'none', outlineOffset: 2 }}>
                                <Icon className="icon" size={32} color="white" />
                            </div>
                            {isOpen && (
                                <div className="plan">
                                    <div className="inner">
                                        <div className="plan-header">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                                <span className="fw-semibold" style={{ fontSize: '0.875rem', color: '#1e293b' }}>{deviceName}</span>
                                                {ptBadge && (
                                                    <span style={{ background: ptBadge.bg, color: ptBadge.color, fontSize: '0.62rem', fontWeight: 700, padding: '2px 6px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                        {ptBadge.label}
                                                    </span>
                                                )}
                                            </div>
                                            <button className="plan-close" onClick={(e) => { e.stopPropagation(); setActiveKey(null); setSelectedEui(null); }}>&times;</button>
                                        </div>
                                        {Object.entries(info).map(([k, v]) => (
                                            <div key={k} className="info-row">
                                                <span className="info-key">{k}</span>
                                                <span className="info-value">{v}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Overlay>
                );
            }

            // Cluster
            const dominantColor = pickDominantColor(items.map(i => i.markerColor));
            const count         = items.length;
            const staleCount    = items.filter(i => i.stale).length;
            const isHighlighted = selectedCoordKey === coordKey && !isOpen;
            return (
                <Overlay key={coordKey} anchor={[lat, lon]} offset={[0, 0]}>
                    <div
                        onClick={(e) => { e.stopPropagation(); setClusterModal({ items, count }); }}
                        style={{ cursor: 'pointer', transform: 'translate(-50%, -100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'auto', position: 'relative' }}
                    >
                        {staleCount > 0 && (
                            <div className="stale-badge">
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                                </svg>
                                {staleCount === count ? 'all 5m+' : `${staleCount} of ${count} 5m+`}
                            </div>
                        )}
                        <div className="map-marker" style={{ background: dominantColor, opacity: 0.3, position: 'absolute', top: 6, left: 6, pointerEvents: 'none' }} />
                        <div className="map-marker" style={{ background: dominantColor, position: 'relative', outline: isHighlighted ? '3px solid #1B4F8A' : 'none', outlineOffset: 2 }}>
                            <Icon className="icon" size={26} color="white" />
                            <span style={{ position: 'absolute', top: -7, right: -7, transform: 'rotate(45deg)', background: '#111827', color: '#fff', fontSize: '0.62rem', fontWeight: 800, width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.45)', lineHeight: 1, border: '1.5px solid #fff' }}>
                                {count}
                            </span>
                        </div>
                    </div>
                </Overlay>
            );
        });
    }, [visibleMapGroups, activeKey, selectedEui, resolvedAll]);

    // Cross-link: selectedEui → pan map + open popup + scroll list
    useEffect(() => {
        if (!selectedEui) return;
        const r = resolvedAll.find(x => x.device?.meta?.deviceEui === selectedEui);
        if (r?.lat != null && r?.lon != null) {
            const key = `${r.lat.toFixed(5)},${r.lon.toFixed(5)}`;
            setMapCenter([r.lat, r.lon]);
            // Auto-open popup only for single-device locations (clusters use the modal)
            const groupSize = resolvedAll.filter(x =>
                x.lat != null && x.lon != null &&
                `${x.lat.toFixed(5)},${x.lon.toFixed(5)}` === key
            ).length;
            if (groupSize === 1) setActiveKey(key);
        }
        const node = listRowRefs.current[selectedEui];
        if (node) {
            const rect = node.getBoundingClientRect();
            if (rect.top < 0 || rect.bottom > window.innerHeight) {
                node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [selectedEui]); // resolvedAll intentionally omitted — panning on device data update is unwanted

    const resetMap = () => {
        if (airportCoordinates?.lat && airportCoordinates?.lon) {
            setMapCenter([parseFloat(airportCoordinates.lat), parseFloat(airportCoordinates.lon)]);
            setZoom(18);
        }
    };

    // Render
    return (
        <div style={{ height: '100%', display: 'flex', overflow: 'hidden' }}>

            {/* Left: location list */}
            <div style={{ width: '36%', minWidth: 260, maxWidth: 420, display: 'flex', flexDirection: 'column', borderRight: '1px solid #dbeafe', overflow: 'hidden' }}>

                {/* Search + summary */}
                <div style={{ padding: '12px 14px 0', flexShrink: 0 }}>
                    <div style={{ position: 'relative' }}>
                        <BsSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#93b4d4', fontSize: 13, pointerEvents: 'none' }} />
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search locations or assets..."
                            style={{ paddingLeft: 32, borderRadius: 8, border: '1px solid #dbeafe', background: '#f0f6ff', fontSize: '0.83rem' }}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, marginBottom: 10 }}>
                        <span style={{ fontSize: '0.72rem', color: '#7aa8d4' }}>
                            {namedEntries.length} location{namedEntries.length !== 1 ? 's' : ''} · {resolvedCount} of {totalAssets} resolved
                        </span>
                        {outOfRange.length > 0 && (
                            <span style={{ fontSize: '0.67rem', fontWeight: 700, background: '#fef3c7', color: '#92400e', borderRadius: 20, padding: '2px 8px', border: '1px solid #fde68a' }}>
                                {outOfRange.length} out of range
                            </span>
                        )}
                    </div>
                </div>

                {/* Cards */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '0 14px 14px' }}>
                    {namedEntries.length === 0 && outOfRange.length === 0 && unresolved.length === 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60%', color: '#93b4d4', fontSize: '0.85rem' }}>
                            {debounceSearch ? 'No matches found' : 'No assets detected'}
                        </div>
                    ) : (
                        <>
                            {namedEntries.map(([title, items]) => (
                                <LocationGroup
                                    key={title}
                                    title={title}
                                    items={items}
                                    selectedEui={selectedEui}
                                    onSelectDevice={eui => { setSelectedEui(eui); setBlinkEui(null); }}
                                    listRowRefs={listRowRefs}
                                    blinkEui={blinkEui}
                                    onBlinkEnd={() => setBlinkEui(null)}
                                />
                            ))}
                            {outOfRange.length > 0 && (
                                <LocationGroup
                                    title="Out of Range"
                                    items={outOfRange}
                                    variant="warning"
                                    subtitle="More than 400 m from any known location"
                                    selectedEui={selectedEui}
                                    onSelectDevice={eui => { setSelectedEui(eui); setBlinkEui(null); }}
                                    listRowRefs={listRowRefs}
                                    blinkEui={blinkEui}
                                    onBlinkEnd={() => setBlinkEui(null)}
                                />
                            )}
                            {unresolved.length > 0 && (
                                <LocationGroup
                                    title="Unresolved"
                                    items={unresolved}
                                    variant="muted"
                                    subtitle="No position data available"
                                    selectedEui={selectedEui}
                                    onSelectDevice={eui => { setSelectedEui(eui); setBlinkEui(null); }}
                                    listRowRefs={listRowRefs}
                                    blinkEui={blinkEui}
                                    onBlinkEnd={() => setBlinkEui(null)}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Right: map */}
            <div style={{ flex: 1, position: 'relative', overflow: 'hidden', borderRadius: 12 }}>
                <Map
                    center={mapCenter}
                    zoom={zoom}
                    onBoundsChanged={({ center, zoom: z, bounds }) => { setMapCenter(center); setZoom(z); if (bounds) setMapBounds(bounds); }}
                    defaultZoom={18}
                    maxZoom={19}
                    minZoom={5}
                    provider={provider}
                    onClick={() => { setActiveKey(null); setSelectedEui(null); }}
                >
                    {deviceMarkers}
                    <ZoomControl />
                </Map>

                {/* Map controls */}
                <div className="map-overlay-controls">
                    <button className="map-overlay-btn" onClick={resetMap} title="Recenter">
                        <FaRegCompass size={16} />
                    </button>
                    <button
                        className="map-overlay-btn"
                        onClick={() => setSatelliteView(p => !p)}
                        title={satelliteView ? 'Switch to Street view' : 'Switch to Satellite view'}
                    >
                        {satelliteView ? <MdMap size={16} /> : <MdSatelliteAlt size={16} />}
                    </button>
                </div>

                {/* Legend */}
                <div className="map-overlay-legend">
                    {LEGEND_ITEMS.map(({ color, label }) => (
                        <div key={label} className="map-overlay-legend-item">
                            <div className="map-overlay-legend-dot" style={{ background: color }} />
                            <span>{label}</span>
                        </div>
                    ))}
                </div>

                {/* Cluster modal */}
                {clusterModal && (
                    <div
                        onClick={() => setClusterModal(null)}
                        style={{ position: 'absolute', inset: 0, zIndex: 500, background: 'rgba(10,18,36,0.55)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <div onClick={(e) => e.stopPropagation()} style={{ width: 500, maxWidth: '94vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.38)', overflow: 'hidden', background: '#fff' }}>
                            {/* Modal header */}
                            <div style={{ background: 'linear-gradient(135deg, #1B4F8A 0%, #2563a8 100%)', padding: '18px 20px 16px', flexShrink: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                                    <div>
                                        <span style={{ background: 'rgba(255,255,255,0.18)', border: '1.5px solid rgba(255,255,255,0.3)', borderRadius: 20, padding: '2px 10px', fontSize: '0.7rem', fontWeight: 700, color: '#fff', letterSpacing: '0.04em' }}>
                                            {clusterModal.count} ASSETS
                                        </span>
                                        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#fff', lineHeight: 1.3, marginTop: 6 }}>Assets at this location</div>
                                        <div style={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.65)', marginTop: 3 }}>{clusterModal.items[0]?.info?.['Location'] ?? '—'}</div>
                                    </div>
                                    <button
                                        onClick={() => setClusterModal(null)}
                                        style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 8, cursor: 'pointer', color: '#fff', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, lineHeight: 1, flexShrink: 0 }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                                    >&times;</button>
                                </div>
                            </div>
                            {/* Modal list */}
                            <div style={{ overflowY: 'auto', flex: 1 }}>
                                {clusterModal.items.map(({ device, markerColor: mc, info, positioningType: dpt, stale }, idx) => {
                                    const dpBadge = POS_BADGE[dpt];
                                    const isEven  = idx % 2 === 0;
                                    return (
                                        <div
                                            key={device.meta.deviceEui}
                                            style={{ display: 'flex', alignItems: 'stretch', background: isEven ? '#fff' : '#f8fafd', borderBottom: '1px solid #eef2f8', transition: 'background 0.1s' }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#eef5ff'}
                                            onMouseLeave={e => e.currentTarget.style.background = isEven ? '#fff' : '#f8fafd'}
                                        >
                                            <div style={{ width: 4, background: mc, flexShrink: 0 }} />
                                            <div style={{ flex: 1, padding: '11px 16px', minWidth: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                                                    <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{device.meta.deviceName}</span>
                                                    {dpBadge && <span style={{ background: dpBadge.bg, color: dpBadge.color, fontSize: '0.6rem', fontWeight: 700, padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0 }}>{dpBadge.label}</span>}
                                                    {stale && <span style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.58rem', fontWeight: 700, padding: '2px 6px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0 }}>5m+</span>}
                                                </div>
                                                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                                                        <span style={{ fontSize: '0.75rem', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>{info['Location'] ?? '—'}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{info['Last Movement'] ?? '—'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {/* Modal footer */}
                            <div style={{ padding: '10px 20px', background: '#f8fafd', borderTop: '1px solid #eef2f8', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Click outside or × to close</span>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    {Object.entries(
                                        clusterModal.items.reduce((acc, { markerColor: mc }) => {
                                            const label = LEGEND_ITEMS.find(l => l.color === mc)?.label ?? 'Other';
                                            acc[label] = (acc[label] ?? 0) + 1;
                                            return acc;
                                        }, {})
                                    ).map(([label, n]) => (
                                        <span key={label} style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>{label}: {n}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AssetTrackingLiveView;
